"use client";

import { m as motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { CalendarDays, Phone, Check } from "lucide-react";

/**
 * Mini simulazione "prenota una call" per il CTA della home: anteprima del
 * flusso TidyCal dei Contatti. Loop in 3 fasi (scegli giorno / scegli slot +
 * telefono / call prenotata). Solo opacity/transform + transition-colors.
 * Con prefers-reduced-motion resta su uno stato statico gia' selezionato.
 * Decorativa: pointer-events-none, aria-hidden.
 */

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven"];
const SLOTS = ["09:30", "11:00", "15:00", "17:30"];
const SEL_DAY = 3; // Gio
const SEL_SLOT = 2; // 15:00

const PILL =
  "rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition-colors duration-300";
const PILL_IDLE = "border-[hsl(var(--border))] text-muted-foreground";
const PILL_ACTIVE =
  "border-primary/40 bg-primary/15 text-primary shadow-[0_0_12px_hsl(var(--primary)/0.25)]";

export function CallBookingAnimation({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    if (reduce) return;
    const durations: Record<number, number> = { 0: 2400, 1: 2600, 2: 2200 };
    const id = window.setTimeout(
      () => setPhase((p) => ((p + 1) % 3) as 0 | 1 | 2),
      durations[phase]
    );
    return () => window.clearTimeout(id);
  }, [phase, reduce]);

  const dayActive = (i: number) => i === SEL_DAY && (reduce || phase >= 0);
  const slotActive = (i: number) => i === SEL_SLOT && (reduce || phase >= 1);
  const filled = reduce || phase >= 1;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none relative w-full select-none overflow-hidden rounded-2xl border border-primary/20 bg-surface p-5 shadow-[0_8px_32px_hsl(var(--primary)/0.10)]"
    >
      {/* header */}
      <div className="mb-4 flex items-center justify-between border-b border-[hsl(var(--border))] pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
            <CalendarDays className="h-3.5 w-3.5 text-primary" />
          </span>
          <span className="text-xs font-medium text-foreground">
            {isItalian ? "Prenota una call" : "Book a call"}
          </span>
        </div>
        <span className="rounded-full border border-[hsl(var(--border))] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground/60">
          30 min
        </span>
      </div>

      {/* step: giorno */}
      <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground/55">
        {isItalian ? "Scegli il giorno" : "Pick a day"}
      </p>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {DAYS.map((d, i) => (
          <span key={d} className={`${PILL} ${dayActive(i) ? PILL_ACTIVE : PILL_IDLE}`}>
            {d}
          </span>
        ))}
      </div>

      {/* step: orario */}
      <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground/55">
        {isItalian ? "Scegli l'orario" : "Pick a time"}
      </p>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {SLOTS.map((s, i) => (
          <span key={s} className={`${PILL} ${slotActive(i) ? PILL_ACTIVE : PILL_IDLE}`}>
            {s}
          </span>
        ))}
      </div>

      {/* step: telefono */}
      <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2">
        <Phone className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span
          className={`text-[11px] transition-colors duration-300 ${
            filled ? "font-medium text-foreground/85" : "text-muted-foreground/50"
          }`}
        >
          {filled
            ? "+39 333 12 34 567"
            : isItalian
            ? "Il tuo telefono"
            : "Your phone"}
        </span>
        {filled && (
          <motion.span
            initial={reduce ? false : { scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.4 }}
            className="ml-auto h-px w-6 origin-left bg-primary/50"
          />
        )}
      </div>

      {/* success overlay */}
      <AnimatePresence>
        {phase === 2 && (
          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-surface/95 supports-[backdrop-filter]:backdrop-blur-sm"
          >
            <motion.span
              initial={reduce ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5, duration: 0.5 }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))] shadow-[0_0_20px_hsl(var(--success)/0.35)]"
            >
              <Check className="h-6 w-6" />
            </motion.span>
            <span className="text-sm font-medium text-foreground">
              {isItalian ? "Call prenotata" : "Call booked"}
            </span>
            <span className="font-mono text-[11px] text-primary">
              {`${DAYS[SEL_DAY]} · ${SLOTS[SEL_SLOT]} · 30 min`}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
